import React, { useCallback, useEffect, useState } from "react";
import { get } from "../lib/api";
import { humanize } from "../lib/format";
import { formatDateTime } from "../../helpers/datetime";
import { Badge, Button, Checkbox, Empty, ErrorBox, Loading } from "../ui";

const ACTION_COLOR = { create: "success", update: "info", delete: "danger", restore: "warning", merge: "primary", purge: "secondary", erase: "secondary" };
const SOURCE_LABEL = { ui: "UI", import: "Import", claude: "Claude", system: "System", cli: "CLI" };
const PAGE_SIZE = 25;

const isInstantKey = (k) => k.endsWith("_at");

export function showValue(key, value) {
    if (value === null || value === undefined || value === "") return <em className="hz-muted">empty</em>;
    if (Array.isArray(value)) return value.length ? value.join(", ") : <em className="hz-muted">none</em>;
    if (typeof value === "boolean") return value ? "yes" : "no";
    if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
    if (isInstantKey(key) && typeof value === "string") return formatDateTime(value, { fallback: value });
    if (typeof value === "object") return <code className="hz-xs">{JSON.stringify(value).slice(0, 160)}</code>;
    const s = String(value);
    return s.length > 140 ? s.slice(0, 140) + "…" : s;
}

function Changes({ entry }) {
    const before = entry.before || {};
    const after = entry.after || {};
    if (entry.action !== "update" && entry.action !== "restore") return null;
    const keys = Array.from(new Set([...Object.keys(before), ...Object.keys(after)])).sort();
    return (
        <div style={{ marginTop: 4, display: "grid", gap: 2 }}>
            {keys.map((k) => (
                <div key={k} className="hz-small">
                    <span className="hz-strong">{humanize(k)}</span>: {showValue(k, before[k])} <i className="ri-arrow-right-line hz-muted"></i> {showValue(k, after[k])}
                </div>
            ))}
        </div>
    );
}

// Record history from GET /v1/crm/audit (who/what: UI, import, Claude, system).
// Leads have no related records to include (stage moves are on the lead).
export default function History({ entityType, entityId, version }) {
    const hasRelated = entityType === "contact" || entityType === "company";
    const [related, setRelated] = useState(hasRelated);
    const [items, setItems] = useState([]);
    const [count, setCount] = useState(0);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const load = useCallback(
        async (p) => {
            setLoading(true);
            setError(null);
            try {
                const res = await get("v1/crm/audit", { entity_type: entityType, entity_id: entityId, include_related: related ? "true" : undefined, page: p, page_size: PAGE_SIZE });
                setItems((prev) => (p === 1 ? res.data || [] : [...prev, ...(res.data || [])]));
                setCount(res.count || 0);
                setPage(p);
            } catch (e) {
                setError(e);
            } finally {
                setLoading(false);
            }
        },
        [entityType, entityId, related]
    );

    useEffect(() => {
        load(1);
    }, [load, version]);

    return (
        <div>
            <div className="hz-card-section hz-row">
                {hasRelated && (
                    <Checkbox checked={related} onChange={setRelated}>
                        Include roles, touches and leads
                    </Checkbox>
                )}
                <span className="hz-spacer"></span>
                <span className="hz-small hz-muted">{count} {count === 1 ? "entry" : "entries"}</span>
            </div>
            <ErrorBox error={error} onRetry={() => load(1)} />
            {loading && items.length === 0 && <Loading />}
            {!loading && items.length === 0 && !error && <Empty>No history yet.</Empty>}
            <div style={{ padding: "0 16px" }}>
                {items.map((e) => (
                    <div key={e.id} style={{ padding: "10px 0", borderBottom: "1px solid var(--hz-divider-soft)" }}>
                        <div className="hz-row-wrap" style={{ gap: 6 }}>
                            <Badge color={ACTION_COLOR[e.action] || "secondary"}>{e.action}</Badge>
                            {e.entity_type !== entityType && <Badge color="secondary">{e.entity_type}</Badge>}
                            <span className="hz-small">{SOURCE_LABEL[e.source] || e.source}{e.actor ? ` · ${e.actor}` : ""}</span>
                            <span className="hz-spacer"></span>
                            <span className="hz-small hz-muted">{formatDateTime(e.at)}</span>
                        </div>
                        <Changes entry={e} />
                    </div>
                ))}
            </div>
            {items.length < count && (
                <div style={{ padding: 16 }}>
                    <Button size="sm" variant="outline" disabled={loading} onClick={() => load(page + 1)}>Show older</Button>
                </div>
            )}
        </div>
    );
}
